/* eslint-disable no-undef */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  X, Save, Trash2, ChevronLeft, ChevronRight, PenLine,
  Type as TypeIcon, Calendar as CalendarIcon, Hash, Loader2,
} from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';
import * as pdfjsLib from 'pdfjs-dist';

// Use the bundled worker URL
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs';

const TOKEN_KEY = 'nexus_token';

const FIELD_TYPES = [
  { k: 'signature', label: 'Signature', icon: PenLine,    w: 0.16, h: 0.05 },
  { k: 'initials',  label: 'Initials',  icon: Hash,        w: 0.06, h: 0.04 },
  { k: 'date',      label: 'Date',      icon: CalendarIcon, w: 0.08, h: 0.03 },
  { k: 'text',      label: 'Text',      icon: TypeIcon,    w: 0.14, h: 0.03 },
];

const TYPE_COLOR = {
  signature: 'rgba(245, 158, 11, 0.70)',   // amber
  initials:  'rgba(16, 185, 129, 0.70)',   // emerald
  date:      'rgba(59, 130, 246, 0.70)',   // blue
  text:      'rgba(168, 85, 247, 0.70)',   // purple
};

/**
 * Drag-to-place signature field editor.
 *
 * Props:
 *   templateId
 *   onClose
 *   onSaved
 */
const TemplateFieldEditor = ({ templateId, onClose, onSaved }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState({ w: 0, h: 0 });
  const [fields, setFields] = useState([]);
  const [pickedType, setPickedType] = useState('signature');
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const dragOffset = useRef({ dx: 0, dy: 0 });
  const resizeStart = useRef({ startX: 0, startY: 0, w: 0, h: 0 });
  const canvasWrap = useRef(null);
  const canvasRef = useRef(null);

  // Load template + PDF
  useEffect(() => {
    if (!templateId) return;
    let alive = true;
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      try {
        // Load template metadata (fields)
        const tRes = await fetch(`${BACKEND_URL}/api/admin/document-templates/${templateId}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!tRes.ok) throw new Error(`Template load: HTTP ${tRes.status}`);
        const t = await tRes.json();
        if (alive) setFields(Array.isArray(t.fields) ? t.fields : []);

        // Load file
        const fRes = await fetch(`${BACKEND_URL}/api/admin/document-templates/${templateId}/file`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!fRes.ok) throw new Error(`PDF load: HTTP ${fRes.status}`);
        const buf = await fRes.arrayBuffer();
        const task = pdfjsLib.getDocument({ data: new Uint8Array(buf) });
        const pdf = await task.promise;
        if (alive) {
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setPage(1);
        }
      } catch (e) {
        if (alive) setLoadErr(e.message);
      }
    })();
    return () => { alive = false; };
  }, [templateId]);

  // Render the active page to canvas
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    try {
      const p = await pdfDoc.getPage(page);
      // target ~840px wide
      const baseVp = p.getViewport({ scale: 1 });
      const scale = Math.min(1.5, Math.max(0.6, 840 / baseVp.width));
      const vp = p.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = vp.width;
      canvas.height = vp.height;
      setPageSize({ w: vp.width, h: vp.height });
      await p.render({ canvasContext: ctx, viewport: vp }).promise;
    } catch (e) {
      toast.error(`Render failed: ${e.message}`);
    }
  }, [pdfDoc, page]);

  useEffect(() => { renderPage(); }, [renderPage]);

  // Drop a new field at a click point
  const onCanvasClick = (e) => {
    if (draggingId || resizingId) return; // ignore clicks ending a drag/resize
    const wrap = canvasWrap.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const xPx = e.clientX - rect.left;
    const yPx = e.clientY - rect.top;
    const tpl = FIELD_TYPES.find(t => t.k === pickedType);
    const xn = Math.max(0, Math.min(1 - tpl.w, (xPx - (tpl.w * pageSize.w) / 2) / pageSize.w));
    const yn = Math.max(0, Math.min(1 - tpl.h, (yPx - (tpl.h * pageSize.h) / 2) / pageSize.h));
    const newField = {
      id: `fld-${Math.random().toString(36).slice(2, 10)}`,
      type: pickedType,
      page,
      x: xn,
      y: yn,
      w: tpl.w,
      h: tpl.h,
      required: true,
      label: null,
    };
    setFields(f => [...f, newField]);
  };

  // Drag a placed field
  const startDrag = (e, f) => {
    e.stopPropagation();
    const wrap = canvasWrap.current.getBoundingClientRect();
    dragOffset.current = {
      dx: e.clientX - wrap.left - f.x * pageSize.w,
      dy: e.clientY - wrap.top - f.y * pageSize.h,
    };
    setDraggingId(f.id);
  };

  // Resize a placed field by dragging the bottom-right handle
  const startResize = (e, f) => {
    e.stopPropagation();
    resizeStart.current = {
      startX: e.clientX,
      startY: e.clientY,
      w: f.w,
      h: f.h,
    };
    setResizingId(f.id);
  };

  useEffect(() => {
    if (!resizingId) return;
    const move = (e) => {
      setFields(arr => arr.map(f => {
        if (f.id !== resizingId) return f;
        const dxN = (e.clientX - resizeStart.current.startX) / pageSize.w;
        const dyN = (e.clientY - resizeStart.current.startY) / pageSize.h;
        return {
          ...f,
          w: Math.max(0.02, Math.min(1 - f.x, resizeStart.current.w + dxN)),
          h: Math.max(0.015, Math.min(1 - f.y, resizeStart.current.h + dyN)),
        };
      }));
    };
    const up = () => { setTimeout(() => setResizingId(null), 0); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [resizingId, pageSize]);

  useEffect(() => {
    if (!draggingId) return;
    const move = (e) => {
      const wrap = canvasWrap.current?.getBoundingClientRect();
      if (!wrap) return;
      setFields(arr => arr.map(f => {
        if (f.id !== draggingId) return f;
        const xPx = e.clientX - wrap.left - dragOffset.current.dx;
        const yPx = e.clientY - wrap.top - dragOffset.current.dy;
        return {
          ...f,
          x: Math.max(0, Math.min(1 - f.w, xPx / pageSize.w)),
          y: Math.max(0, Math.min(1 - f.h, yPx / pageSize.h)),
        };
      }));
    };
    const up = () => { setTimeout(() => setDraggingId(null), 0); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [draggingId, pageSize]);

  const deleteField = (id) => setFields(arr => arr.filter(f => f.id !== id));

  const save = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${BACKEND_URL}/api/admin/document-templates/${templateId}/fields`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fields }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `HTTP ${res.status}`);
      }
      toast.success(`Saved ${fields.length} field(s)`);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const fieldsOnPage = fields.filter(f => f.page === page);

  return (
    <div className="fixed inset-0 z-[90] bg-slate-950/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
         data-testid="tpl-field-editor">
      <div className="bg-[#0B1220] border border-slate-700 rounded-lg w-full max-w-5xl shadow-2xl flex flex-col max-h-[96vh]">
        {/* Header */}
        <div className="border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Drag-to-Place Field Editor</p>
            <h2 className="text-white text-base font-bold">Mark signature, initial, date and text fields</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="tpl-editor-close">
            <X size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="border-b border-slate-700 px-3 py-2 flex items-center flex-wrap gap-2 flex-shrink-0">
          {FIELD_TYPES.map(t => {
            const Icon = t.icon;
            const active = pickedType === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setPickedType(t.k)}
                className={`text-xs px-3 py-1.5 rounded border ${active ? 'bg-amber-500 border-amber-500 text-slate-900 font-bold' : 'bg-slate-800 border-slate-700 text-slate-200'}`}
                data-testid={`tpl-type-${t.k}`}
              >
                <Icon size={12} className="inline mr-1" /> {t.label}
              </button>
            );
          })}
          <span className="text-[10px] text-slate-500 font-mono ml-2">click on PDF to place · drag boxes to move · click the × to delete</span>
        </div>

        {/* Page selector */}
        <div className="border-b border-slate-700 px-3 py-2 flex items-center justify-between flex-wrap gap-2 flex-shrink-0">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="text-slate-200 h-7 px-2" data-testid="tpl-page-prev">
              <ChevronLeft size={14} />
            </Button>
            <span className="text-xs text-slate-300 font-mono">Page {page} / {numPages || '…'}</span>
            <Button size="sm" variant="ghost" disabled={page >= numPages} onClick={() => setPage(p => Math.min(numPages, p + 1))} className="text-slate-200 h-7 px-2" data-testid="tpl-page-next">
              <ChevronRight size={14} />
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">{fields.length} field(s) placed · {fieldsOnPage.length} on this page</p>
        </div>

        {/* PDF + overlay */}
        <div className="flex-1 overflow-auto p-4 bg-slate-900/70 flex items-start justify-center">
          {loadErr ? (
            <p className="text-rose-200 text-sm p-4">Load error: {loadErr}</p>
          ) : !pdfDoc ? (
            <p className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading PDF…</p>
          ) : (
            <div
              ref={canvasWrap}
              className="relative inline-block shadow-2xl"
              style={{ cursor: 'crosshair' }}
              onClick={onCanvasClick}
              data-testid="tpl-canvas-wrap"
            >
              <canvas ref={canvasRef} className="block bg-white" />
              {fieldsOnPage.map(f => (
                <div
                  key={f.id}
                  className="absolute border-2 rounded flex items-center justify-center text-[10px] font-bold text-slate-900 select-none"
                  style={{
                    left: `${f.x * pageSize.w}px`,
                    top: `${f.y * pageSize.h}px`,
                    width: `${f.w * pageSize.w}px`,
                    height: `${f.h * pageSize.h}px`,
                    background: TYPE_COLOR[f.type] || TYPE_COLOR.signature,
                    borderColor: '#0F172A',
                    cursor: draggingId === f.id ? 'grabbing' : (resizingId === f.id ? 'nwse-resize' : 'grab'),
                  }}
                  onMouseDown={(e) => startDrag(e, f)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`tpl-field-${f.id}`}
                >
                  <span className="pointer-events-none">{f.type.toUpperCase()}</span>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); deleteField(f.id); }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-rose-600 text-white text-[10px] rounded-full flex items-center justify-center hover:bg-rose-500"
                    data-testid={`tpl-field-delete-${f.id}`}
                  >
                    ×
                  </button>
                  <div
                    onMouseDown={(e) => startResize(e, f)}
                    className="absolute -bottom-1 -right-1 w-3 h-3 bg-slate-900 border border-white rounded-sm cursor-nwse-resize"
                    style={{ cursor: 'nwse-resize' }}
                    data-testid={`tpl-field-resize-${f.id}`}
                    title="Drag to resize"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-700 px-4 py-3 flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="text-slate-200 h-9" data-testid="tpl-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white h-9" data-testid="tpl-save">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            <span className="ml-1.5">Save Fields ({fields.length})</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateFieldEditor;
